import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { Box, Typography, Select, MenuItem, InputBase, useTheme } from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { getFormStepStore } from './useFormStepStore';
import DynamicFormFieldDesigner from '../pages/forms/DynamicFormFieldDesigner';
import CoverImageSetting from '../pages/forms/CoverImageSetting';
import { useFormPalette } from '../pages/forms/DynamicFormPrimitives';
import { NODE_UI } from '../NodeUI/nodeUiStyles';
import PublicAccessPolicyEditor from '../access/PublicAccessPolicyEditor';
import useTaskInboxStore from '../pages/useTaskInboxStore';
import FlowSettingsBlock from '../FormFlow/FlowSettingsBlock';
import StepperTitleInput from '../FormFlow/StepperTitleInput';
import CreationOutputPlaceholderField from '../CreationOutputPlaceholder/CreationOutputPlaceholderField';
import useUpstreamFlowEntries from '../FormFlow/useUpstreamFlowEntries';

const RESPONSE_MODE_DESCRIPTIONS = {
  FILL_SUBMIT: 'Users fill out the step and submit. No drafts are saved on the server.',
  FILL_REEDIT: 'Users can save drafts and return via the resume URL to continue editing.',
  VIEW_ONLY: 'Users can view the step but cannot submit.',
};

function SectionSelect({ label, value, onChange, options, theme, palette }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <Typography
        component="label"
        sx={{
          fontSize: NODE_UI.fontSize.label,
          fontWeight: NODE_UI.fontWeight.label,
          color: palette.textSecondary,
          lineHeight: 1.4,
          userSelect: 'none',
        }}
      >
        {label}
      </Typography>
      <Select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        sx={{
          fontFamily: 'inherit',
          fontSize: NODE_UI.fontSize.value,
          color: palette.textPrimary,
          backgroundColor: theme.palette.background.default,
          borderRadius: NODE_UI.borderRadiusSm,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: palette.border },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: palette.textSecondary },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: palette.textPrimary },
          '& .MuiSelect-select': { padding: NODE_UI.input.padding },
        }}
      >
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
        ))}
      </Select>
    </Box>
  );
}

function SettingsSection({ title, children, palette }) {
  return (
    <Box
      sx={{
        border: `1px solid ${palette.border}`,
        borderRadius: NODE_UI.borderRadius,
        backgroundColor: palette.surface,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: '12px',
          py: '7px',
          backgroundColor: palette.surfaceSubtle,
          borderBottom: `1px solid ${palette.border}`,
        }}
      >
        <Typography sx={{ fontSize: NODE_UI.fontSize.label, fontWeight: 600, color: palette.textPrimary }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {children}
      </Box>
    </Box>
  );
}

function TabBar({ tabs, active, onChange, palette }) {
  return (
    <Box sx={{
      display: 'flex', gap: '2px', p: '3px',
      backgroundColor: palette.surfaceSubtle,
      borderRadius: '8px', border: `1px solid ${palette.border}`, mb: '12px',
    }}>
      {tabs.map((tab, i) => (
        <Box key={tab} onClick={() => onChange(i)} sx={{
          flex: 1, textAlign: 'center', py: '5px', borderRadius: '6px', cursor: 'pointer',
          fontSize: NODE_UI.fontSize.value,
          fontWeight: active === i ? 600 : 400,
          color: active === i ? palette.textPrimary : palette.textSecondary,
          backgroundColor: active === i ? palette.surface : 'transparent',
          boxShadow: active === i ? NODE_UI.shadow : 'none',
          transition: 'all 0.15s ease', userSelect: 'none',
        }}>
          {tab}
        </Box>
      ))}
    </Box>
  );
}

export default function FormStepTemplate({
  component_id,
  projectId,
  setChildTemplateLoaded,
  availablePlaceholders,
}) {
  const store = useMemo(() => getFormStepStore(component_id), [component_id]);
  const data = store((s) => s.data);
  const loaded = store((s) => s.loaded);
  const componentName = store((s) => s.name);
  const palette = useFormPalette();
  const theme = useTheme();

  const [tab, setTab] = useState(0);

  const activeAccountUsers = useTaskInboxStore((s) => s.activeAccountUsers);
  const activeAccountUsersLoading = useTaskInboxStore((s) => s.activeAccountUsersLoading);
  const fetchActiveAccountUsers = useTaskInboxStore((s) => s.fetchActiveAccountUsers);

  useEffect(() => {
    if (data.accessPolicy?.mode === 'AUTHENTICATED_SELECTED_USERS') {
      fetchActiveAccountUsers();
    }
  }, [data.accessPolicy?.mode, fetchActiveAccountUsers]);

  useEffect(() => {
    if (loaded && setChildTemplateLoaded) setChildTemplateLoaded(true);
  }, [loaded, setChildTemplateLoaded]);

  const handleFieldsChange = useCallback((fields) => store.getState().setFields(fields), [store]);
  const handleTitleChange = useCallback((val) => store.getState().setTitle(val), [store]);
  const handleDescriptionChange = useCallback((val) => store.getState().setDescription(val), [store]);
  const handleAccessPolicyChange = useCallback((val) => store.getState().setAccessPolicy(val), [store]);
  const handleResponseModeChange = useCallback((val) => store.getState().setResponseMode(val), [store]);
  const handleCoverImageChange = useCallback(
    (val) => store.getState().setFormSettings({ coverImageUrl: val }),
    [store],
  );
  const handleFlowSettingsChange = useCallback(
    (patch) => store.getState().setFlowSettings(patch),
    [store],
  );
  const handleStepperTitleChange = useCallback(
    (val) => store.getState().setStepperTitle(val),
    [store],
  );
  const handleCreationOutputKeyChange = useCallback(
    (key) => store.getState().setCreationOutputPlaceholderKey(key),
    [store],
  );

  const upstreamFlowEntries = useUpstreamFlowEntries(component_id);

  if (!loaded) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <TabBar
        tabs={['Form Design', 'Settings']}
        active={tab}
        onChange={setTab}
        palette={palette}
      />

      {tab === 0 && (
        <DynamicFormFieldDesigner
          fields={data.fields}
          onChange={handleFieldsChange}
          title={data.title}
          description={data.description}
          onTitleChange={handleTitleChange}
          onDescriptionChange={handleDescriptionChange}
          availablePlaceholders={availablePlaceholders}
        />
      )}

      {tab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <SettingsSection title="General" palette={palette}>
            <PublicAccessPolicyEditor
              accessPolicy={data.accessPolicy}
              onChange={handleAccessPolicyChange}
              users={activeAccountUsers}
              usersLoading={activeAccountUsersLoading}
            />
            <SectionSelect
              label="Response Mode"
              value={data.responseMode}
              onChange={handleResponseModeChange}
              options={[
                { value: 'FILL_SUBMIT', label: 'Submit only' },
                { value: 'FILL_REEDIT', label: 'Save & resume drafts' },
                { value: 'VIEW_ONLY', label: 'View only' },
              ]}
              theme={theme}
              palette={palette}
            />
            <Typography sx={{ fontSize: NODE_UI.fontSize.caption, color: palette.textSecondary, opacity: 0.75 }}>
              {RESPONSE_MODE_DESCRIPTIONS[data.responseMode] || ''}
            </Typography>
          </SettingsSection>

          <SettingsSection title="On creation output" palette={palette}>
            <CreationOutputPlaceholderField
              componentType="form_step"
              value={data.creationOutputPlaceholder}
              onChange={handleCreationOutputKeyChange}
            />
          </SettingsSection>

          <SettingsSection title="Appearance" palette={palette}>
            <CoverImageSetting
              value={data.formSettings?.coverImageUrl || ''}
              onChange={handleCoverImageChange}
            />
          </SettingsSection>

          <SettingsSection title="Flow" palette={palette}>
            <FlowSettingsBlock
              componentId={component_id}
              componentName={componentName}
              componentData={data}
              upstreamFlowEntries={upstreamFlowEntries}
              onChange={handleFlowSettingsChange}
            />
            <StepperTitleInput
              value={data.stepperTitle}
              onChange={handleStepperTitleChange}
            />
          </SettingsSection>
        </Box>
      )}
    </Box>
  );
}
